/**
 * @fileoverview Accept-invitation mutation hook.
 * @description Wraps POST /identity/invitations/accept (the "set your first password" call) in a
 * TanStack mutation. Accepting registers the account and emails an activation code rather than
 * signing the user in, so on success this stores the returned activation challenge and navigates
 * to /two-factor via the shared openActivationChallenge helper — that page's form verifies the
 * code against the activation endpoint, then sends the user to /login to sign in.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { acceptInvitation } from '@/features/auth/services/invitation.service'
import { openActivationChallenge } from '@/shared/lib/auth/two-factor-challenge.utils'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
} from '@/features/auth/services/invitation.service'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Submits the chosen password to redeem an invitation and, on success, continues to
 * the activation-code step.
 * @returns {ReturnType<typeof useMutation<AcceptInvitationResponse, ApiError,
 * AcceptInvitationRequest>>} The mutation object (mutate, isPending, error, ...).
 */
export function useAcceptInvitation() {
  const { setChallenge } = useTwoFactorChallenge()
  const navigate = useNavigate()

  return useMutation<AcceptInvitationResponse, ApiError, AcceptInvitationRequest>({
    mutationFn: acceptInvitation,
    onSuccess: (response) => {
      openActivationChallenge(setChallenge, navigate, response)
    },
  })
}
